// The app-key keyring (ADR-0061 Part 2).
//
// A `kid`-keyed set of AES-256 keys: one ACTIVE key that signs new writes, plus
// every historical key still needed to READ un-migrated rows. In steady state
// during a rotation that is {active, retiring}; the general form is the active key
// plus every past `kid` that still has rows.
//
// Two properties are load-bearing and easy to get wrong:
//
//   1. It tracks the bootstrap TTL — it does NOT memoise forever. The secrets
//      bootstrap rewrites process.env on a blocking TTL so a rotated key reaches
//      warm containers within one TTL (secrets-bootstrap.ts). The ES256 signer's
//      memoise-once keystore would REGRESS that: a warm container would keep
//      stamping the stale active kid after the histogram reads clean, re-dirtying
//      the corpus. So we read process.env fresh each call and cache only the
//      hex→(key,kid) DERIVATION, keyed by the hex value — a rotated key is a new
//      hex string and thus a natural cache miss, re-derived on the next call.
//
//   2. Writes select the ACTIVE slot ONLY. "Decrypt-only" for the retiring key is
//      a convention with no structural enforcement on a symmetric key (unlike
//      ES256, whose previous key is published public-only and cannot sign), so it
//      is enforced HERE in code: `requireActiveKey()` never falls back to the
//      retiring slot. Falling back would stamp new writes under a kid that gets
//      dropped at drain end → silent data loss.

import { deriveKid } from './envelope.js';
import { AppKeyNotInKeyringError } from './secret-errors.js';

/** Env var holding the active key (hex, 32 bytes / 64 chars). */
const ACTIVE_ENV = 'AUTH_ENCRYPTION_KEY';
/** Env var holding the retiring, decrypt-only key present during a rotation drain. */
const PREVIOUS_ENV = 'AUTH_ENCRYPTION_KEY_PREVIOUS';

const HEX_KEY_RE = /^[0-9a-fA-F]{64}$/;

export interface KeyEntry {
  /** First 8 hex of SHA-256(key). */
  readonly kid: string;
  readonly key: Buffer;
}

export interface Keyring {
  /** The key new writes are sealed under. `null` if unset/invalid. */
  readonly active: KeyEntry | null;
  /** Every key by `kid` (active + retiring + any historical), for scheme-a/v1 reads. */
  readonly byKid: ReadonlyMap<string, KeyEntry>;
  /** Trial order for keyless (v1) blobs: active first, then the rest. */
  readonly trialOrder: readonly KeyEntry[];
}

// hex value → derived entry. Keyed by the hex so a rotated key value misses and
// is re-derived; stale entries linger harmlessly (only ever a handful of keys).
const derivationCache = new Map<string, KeyEntry>();

/** Validate + derive a key from a hex string, memoising the derivation. */
function deriveEntry(hex: string | undefined): KeyEntry | null {
  if (!hex || !HEX_KEY_RE.test(hex)) return null;
  const cached = derivationCache.get(hex);
  if (cached) return cached;
  const key = Buffer.from(hex, 'hex');
  const entry: KeyEntry = { kid: deriveKid(key), key };
  derivationCache.set(hex, entry);
  return entry;
}

/**
 * Build the current keyring from process.env. Cheap — only SHA-256s a key the
 * first time its hex value is seen. Call per-decrypt so a rotation propagates
 * within the bootstrap TTL.
 */
export function getKeyring(): Keyring {
  const active = deriveEntry(process.env[ACTIVE_ENV]);
  const retiring = deriveEntry(process.env[PREVIOUS_ENV]);

  const byKid = new Map<string, KeyEntry>();
  const trialOrder: KeyEntry[] = [];
  for (const entry of [active, retiring]) {
    if (!entry) continue;
    if (!byKid.has(entry.kid)) {
      byKid.set(entry.kid, entry);
      trialOrder.push(entry); // active pushed first ⇒ tried first
    }
  }

  return { active, byKid, trialOrder };
}

/**
 * The active key for a WRITE. Throws `AppKeyNotInKeyringError` if it is absent —
 * never falls back to the retiring slot (that would stamp a doomed kid).
 */
export function requireActiveKey(): KeyEntry {
  const active = getKeyring().active;
  if (!active) {
    throw new AppKeyNotInKeyringError(`${ACTIVE_ENV} is not set or is not a 64-char hex string`);
  }
  return active;
}

/** Resolve a specific key by `kid` (scheme-a / v2 read path). */
export function resolveKeyByKid(kid: string): KeyEntry | undefined {
  return getKeyring().byKid.get(kid);
}

/** Test-only: clear the hex→entry derivation cache so env mutations take effect. */
export function __resetKeyringCacheForTests(): void {
  derivationCache.clear();
}
